import { lazy, ReactNode } from 'react'
import * as constants from 'resources/constants'
import { Role } from 'resources/types'

const UsersIcon = lazy(() => import('@mui/icons-material/People'))
const PlacesIcon = lazy(() => import('@mui/icons-material/Place'))
const RegionIcon = lazy(() => import('assets/RegionIcon'))
const CommunityIcon = lazy(() => import('@mui/icons-material/Signpost'))
const ProfileIcon = lazy(() => import('@mui/icons-material/AccountCircle'))
const RewardsIcon = lazy(() => import('@mui/icons-material/EmojiEvents'))
const BonusLevelIcon = lazy(() => import('@mui/icons-material/WorkspacePremium'))
const MissingAccountIcon = lazy(() => import('assets/MissingAccount'))
const AssignedToMeIcon = lazy(() => import('@mui/icons-material/HowToReg'))
const CompanyWebsiteIcon = lazy(() => import('@mui/icons-material/Public'))
const AutoNotificationsIcon = lazy(() => import('@mui/icons-material/Campaign'))
const NotificationsIcon = lazy(() => import('@mui/icons-material/Notifications'))
const NotificationCampaignsIcon = lazy(() => import('@mui/icons-material/AddAlert'))
const CalendarIcon = lazy(() => import('@mui/icons-material/CalendarMonth'))
const ClientRegistrationIcon = lazy(() => import('@mui/icons-material/AppRegistration'))
const AdminClientRegistrationIcon = lazy(() => import('@mui/icons-material/AssignmentTurnedIn'))
const AdminSettingIcon = lazy(() => import('@mui/icons-material/AdminPanelSettings'))
const InformationIcon = lazy(() => import('@mui/icons-material/Info'))
const MyPointsIcon = lazy(() => import('@mui/icons-material/TrendingUp'))
const PointsReportIcon = lazy(() => import('@mui/icons-material/AssessmentOutlined'))

export const companyDynaicString = '__COMPANY_WEBSITE__'

export interface SidebarOptionType {
	title: string;
	icon: ReactNode;
	path?: string;
	hasChildren?: boolean;
	nested?: boolean;
	roles: Role[];
	children?: SidebarOptionType[];
	hasDividerAbove?: boolean
	hasDivderBelow?: boolean
	hideFromLanding?: boolean
}

const SidebarOptions: SidebarOptionType[] = [
	{
		title: companyDynaicString,
		icon: <CompanyWebsiteIcon />,
		path: companyDynaicString,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES, Role.REALTOR],
		hideFromLanding: true,
	},
	{
		title: 'Notifications',
		icon: <NotificationsIcon />,
		path: constants.notificationsURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES, Role.REALTOR],
	},
	{
		title: 'My Points',
		icon: <MyPointsIcon />,
		path: constants.myPointsURL,
		roles: [Role.REALTOR],
	},
	{
		title: 'Realtors',
		icon: <UsersIcon />,
		path: constants.realtorsPage,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES],
	},
	{
		title: 'Assigned to Me',
		icon: <AssignedToMeIcon />,
		path: constants.assignedToMeURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER],
	},
	{
		title: 'Events',
		icon: <CalendarIcon />,
		path: constants.eventsURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES, Role.REALTOR],
	},
	{
		title: 'Registration',
		icon: <ClientRegistrationIcon />,
		path: constants.clientRegistrationPageURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES, Role.REALTOR],
	},
	{
		title: 'Information',
		icon: <InformationIcon />,
		path: constants.informationURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES, Role.REALTOR],
	},
	{
		title: 'Profile',
		icon: <ProfileIcon />,
		path: constants.profilePage,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES, Role.REALTOR],
		hasDividerAbove: true,
		hasDivderBelow: true,
	},
	{
		title: 'Places',
		icon: <PlacesIcon />,
		hasChildren: true,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN, Role.MANAGER, Role.SALES],
		children: [
			{
				title: 'Regions',
				icon: <RegionIcon />,
				path: constants.regionsURL,
				nested: true,
				roles: [Role.ADMIN, Role.ACCOUNT_OWNER, Role.MANAGER, Role.SALES],
			},
			{
				title: 'Communtiies',
				icon: <CommunityIcon />,
				path: constants.communitiesURL,
				nested: true,
				roles: [Role.ADMIN, Role.ACCOUNT_OWNER, Role.MANAGER, Role.SALES],
			},
		],
	},
	{
		title: 'Users',
		icon: <UsersIcon />,
		hasChildren: true,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
		children: [
			{
				title: 'Manage',
				icon: <UsersIcon />,
				path: constants.manageUsersPage,
				nested: true,
				roles: [Role.ADMIN, Role.ACCOUNT_OWNER],
			},
			{
				title: 'Unclaimed Points',
				icon: <MissingAccountIcon />,
				path: constants.unclaimedPointsURL,
				nested: true,
				roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
			},
			{
				title: 'Send Campaigns',
				icon: <NotificationCampaignsIcon />,
				path: constants.notificationCampaignsURL,
				nested: true,
				roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
			},
			{
				title: 'View Registrations',
				icon: <AdminClientRegistrationIcon />,
				path: constants.adminClientRegistrationPageURL,
				nested: true,
				roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
			},
		],
	},
	{
		title: 'Rewards',
		icon: <RewardsIcon />,
		path: constants.rewardsURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
	},
	{
		title: 'Bonus Level',
		icon: <BonusLevelIcon />,
		path: constants.bonusLevelURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
	},
	{
		title: 'Auto Notifications',
		icon: <AutoNotificationsIcon />,
		path: constants.autoNotificationsURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
	},
	{
		title: 'Reports',
		icon: <PointsReportIcon />,
		path: constants.pointsReportsURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
	},
	{
		title: 'Settings',
		icon: <AdminSettingIcon />,
		path: constants.adminSettingsURL,
		roles: [Role.ACCOUNT_OWNER, Role.ADMIN],
	},
]

export default SidebarOptions
